import { createSignal } from "solid-js";
import { api, TOKEN_STORAGE_KEY, type User } from "./api";

/**
 * JWT-based auth state. The token lives in localStorage (and is injected into
 * every API request by the client). Only the seeded admin/admin user can log
 * in for now, but the flow is real JWT.
 */

function readToken(): string | null {
    try {
        return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
        return null;
    }
}

function writeToken(token: string | null) {
    try {
        if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
        else localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

const [token, setToken] = createSignal<string | null>(readToken());
const [currentUser, setCurrentUser] = createSignal<User | null>(null);

const isLoggedIn = () => token() !== null;

/** Authenticate with username/password; throws on invalid credentials. */
async function signIn(username: string, password: string): Promise<User> {
    const { data, error } = await api.POST("/auth/login", {
        body: { username, password },
    });
    if (error || !data?.token) {
        throw new Error("Kullanıcı adı veya şifre hatalı");
    }
    writeToken(data.token);
    setToken(data.token);
    setCurrentUser(data.user ?? null);
    return data.user as User;
}

/** Clear the session (server is stateless; the client just drops the token). */
function signOut() {
    void api.POST("/auth/logout", {});
    writeToken(null);
    setToken(null);
    setCurrentUser(null);
}

/** Load the current user from the token, if any. Call once on app start. */
async function refreshCurrentUser(): Promise<void> {
    if (!token()) {
        setCurrentUser(null);
        return;
    }
    const { data, error } = await api.GET("/auth/me", {});
    if (error || !data) {
        // Token invalid/expired: clear it.
        writeToken(null);
        setToken(null);
        setCurrentUser(null);
        return;
    }
    setCurrentUser(data);
}

export {
    token,
    currentUser,
    isLoggedIn,
    signIn,
    signOut,
    refreshCurrentUser,
};
